const { default: Log } = require('../../common/log');
var Util = require('../util');
var version = require('../version');
var AdapterBase = require('./adapter-base');
var AdapterConstants = require('../constants/adapter');
var MD5 = require('../deviceUUID/md5');
const { AnalyticsTag } = require('../../common/Constants');
const { default: Core } = require('../../core/Core');
const { default: CoreConstants } = require('../../core/utils/CoreConstants');

/**
 * Adapter class supporting both JSON and class-based adapters (With window.Function).
 * This version uses window.Function for JSON adapter evaluation.
 * 
 * Extends AdapterBase with JSON and class loading logic.
 */
var Adapter = AdapterBase.extend(
  /** @lends npaw.Adapter.prototype */
  {
    /**
     * Main Adapter class supporting both JSON and class-based adapters.
     *
     * @constructs Adapter
     * @extends npaw.AdapterBase
     * @memberof npaw
     *
     * @param {string} videoKey
     * @param {object} videoObj
     * @param {object} plugin
     * @param {object} adapterTemplates
     * @param {object|string} player Either an instance of the player or a string containing an ID.
     * @param {string|object} adapter JSON string or adapter class instance
     */
    constructor: function (videoKey, videoObj, plugin, adapterTemplates, player, adapter) {
      // Initialize base adapter
      this._initBase(videoKey, videoObj, plugin, player);

      /** Define adapter json */
      const isJsonAdapter = typeof adapter !== 'object';
      let adapterJson;
      const adapterCustomerDefined = isJsonAdapter ? JSON.parse(adapter) : undefined;

      /** Set Adapter Name */
      this._adapterName = !isJsonAdapter
        ? this.getAdapterNameFromClass(adapter)
        : this.getAdapterName(adapterCustomerDefined);

      if (isJsonAdapter && this.plugin && this.plugin.canOverwriteAdapters()) {
        adapterJson = this._getAdapterFromTemplates(this._adapterName);
        if (adapterJson) {
          Log.notice(AnalyticsTag, 'Overwrite Adapter: ' + this._adapterName);
        }
      }

      if (isJsonAdapter && !adapterJson) {
        if (!adapter) {
          adapterJson = this._getAdapterFromTemplates(this._adapterName);
        } else {
          try {
            adapterJson = adapterCustomerDefined;
          } catch (err) {
            Log.warn(AnalyticsTag, 'Problem parsing adapter json code (to object conversion)');
          }
        }
      }

      /** Reference to the video/object tag, could be the same as the player. */
      this.tag = this.player;

      if (isJsonAdapter && adapterJson) {
        // Eval adapter Json
        this.evalAdapterJson(adapterJson, false);

        // Register listeners
        this.registerListeners();
      } else if (!isJsonAdapter && adapter) {
        // Load adapter from given Javascript class
        this.loadAdapterClasses(adapter, false);
        // Register listeners
        this.registerListeners();
      }

      if (this.plugin.options.enableExtraMetricCollection) {
        this._npawVideo.fetchLocation();
      }

      Log.notice(AnalyticsTag, 'Adapter ' + this.getVersion() + ' with Lib ' + version + ' is ready.');
    },

    /**
     * Get adapter name from JSON adapter object
     * @param adapterObject
     */
    getAdapterName: function (adapterObject) {
      if (adapterObject && adapterObject['adapter,getVersion']) {
        try {
          var getVersionMethod = window.Function(adapterObject['adapter,getVersion']);
          return getVersionMethod();
        } catch (e) {
          Log.debug(AnalyticsTag, 'Error getting adapter version from class:', e);
        }
      }
      return 'undefined';
    },

    /**
     * Get adapter name from class
     * @param adapterClass
     */
    getAdapterNameFromClass: function (adapterClass) {
      if (adapterClass && adapterClass['getVersion']) {
        try {
          return adapterClass['getVersion']();
        } catch (e) {
          Log.debug(AnalyticsTag, 'Error getting adapter version from class:', e);
        }
      }
      return 'undefined';
    },

    /**
     * Update Adapter templates, and try to override methods
     * @param adapterTemplates
     */
    updateAdapterTemplates: function (adapterTemplates) {
      if (this.plugin.canOverwriteAdapters()) {
        /** Define adapter json */
        var adapterJson = this._getAdapterFromTemplates(this._adapterName, adapterTemplates);

        if (adapterJson) {
          // Eval adapter Json
          this.evalAdapterJson(adapterJson, true);

          // Register listeners
          this.registerListeners();
        }
      }
    },

    /**
     * Get adapter from templates
     * @param adapterName
     * @param adapterTemplates
     */
    _getAdapterFromTemplates: function (adapterName, adapterTemplates) {
      var adapterJson = null;
      if (!adapterTemplates) {
        // If adapter templates is empty, get it from plugin adapter templates object
        adapterTemplates = this.plugin.adapterTemplates;
      }
      try {
        if (adapterName && adapterTemplates && adapterTemplates[adapterName.toLowerCase()]) {
          adapterJson = JSON.parse(adapterTemplates[adapterName.toLowerCase()]);
        }
      } catch (err) {
        Log.warn(AnalyticsTag, 'Adapter ' + adapterName + ' not found on repository');
      }
      return adapterJson;
    },

    /**
     * Evaluate JSON adapter using window.Function
     * @param adapterJson
     * @param loadFromTemplate
     */
    evalAdapterJson: function (adapterJson, loadFromTemplate) {
      loadFromTemplate = loadFromTemplate || false;
      var integratedAdapterMethods = 0;
      var integratedExternalMethods = 0;
      var lastEvaluatedMethod = '';
      try {
        for (var method in adapterJson) {
          var methodParts = method.split(',');
          var context = methodParts.shift();
          var methodName = methodParts.shift();
          lastEvaluatedMethod = methodName;
          var adapterFunction = null;
          if (methodParts && methodParts.length >= 1) {
            var methodArguments = methodParts;
            adapterFunction = window.Function(methodArguments, adapterJson[method]);
          } else {
            adapterFunction = window.Function(adapterJson[method]);
          }
          if (adapterFunction) {
            if (context.toLowerCase() === 'adapter') {
              this[methodName] = adapterFunction;
              integratedAdapterMethods++;
            } else {
              if (context.indexOf('.adapter') > 0) {
                context = context.replace('.adapter', '');
                if (!this.getAdapterClasses()[context]) {
                  // eslint-disable-next-line no-useless-constructor
                  this.getAdapterClasses()[context] = new Adapter(
                    this._key,
                    this._npawVideo,
                    this.getPlugin(),
                    null,
                    this.player,
                    null,
                    null,
                    {}
                  );
                }
                this.getAdapterClasses()[context][methodName] = adapterFunction;
                integratedExternalMethods++;
              } else {
                if (!this.getAdapterClasses()[context]) {
                  // eslint-disable-next-line no-useless-constructor
                  this.getAdapterClasses()[context] = {};
                  this.getAdapterClasses()[context].constructor = function () {};
                  // create util to get npaw utils and set the same adapter plugin
                  this.getAdapterClasses()[context].plugin = this.plugin;
                  this.getAdapterClasses()[context].getNpawUtils = function () {
                    return this.plugin.utils;
                  };
                }
                this.getAdapterClasses()[context][methodName] = adapterFunction;
                integratedExternalMethods++;
              }
            }
          }
        }
        this._loadedAdapterImplementation = true;
        // Set the adapter Json Code
        this._adapterEvaluatedCode = adapterJson;
        // Set the adapter Hash value
        try {
          if (typeof adapterJson === 'string') {
            this.adapterHash = MD5(adapterJson.trim());
          } else {
            this.adapterHash = MD5(JSON.stringify(adapterJson).trim());
          }
        } catch (err) {
          this.adapterHash = 'Undefined';
        }
        // Show Adapter loaded
        Log.notice(
          AnalyticsTag,
          '[' +
            this.getAdapterKey() +
            '] Loaded adapter (' +
            this.getVersion() +
            ') (Hash ' +
            this.adapterHash +
            ') ' +
            'implementation from JSON; From templates: ' +
            loadFromTemplate +
            '; Integrated adapter methods: ' +
            integratedAdapterMethods +
            ', external methods: ' +
            integratedExternalMethods
        );
      } catch (err) {
        Log.error(
          AnalyticsTag,
          "Can't be evaluated adapter json correctly; From templates: " +
            loadFromTemplate +
            ', Last method evaluated: ' +
            lastEvaluatedMethod
        );
      }
    },

    /**
     * Load the adapter provided by the Javascript class
     * @param adapterClass
     * @param loadFromTemplate
     */
    loadAdapterClasses: function (adapterClass, loadFromTemplate) {
      loadFromTemplate = loadFromTemplate || false;
      let integratedAdapterMethods = 0;
      let integratedExternalMethods = 0;
      let lastEvaluatedMethod = '';
      let prototype = Object.getPrototypeOf(adapterClass);

      try {
        let adapterString = '';
        const listOfMethods = new Map();
        while (prototype !== Object.prototype) {
          Object.getOwnPropertyNames(prototype).forEach((prop) => {
            if (typeof prototype[prop] === 'function' && prop !== 'constructor' && !listOfMethods.has(prop)) {
              listOfMethods.set(prop, prototype[prop]);
            }
          });
          prototype = Object.getPrototypeOf(prototype);
        }
        listOfMethods.forEach((value, key) => {
          this[key] = value;
          adapterString += value.toString();
          integratedAdapterMethods++;
        });
        lastEvaluatedMethod = 'Adapter Class';
        // Convert ads adapter from the video adapter into ads adapters
        if (typeof adapterClass.adsAdapters !== 'undefined') {
          for (const adsAdapter of Object.keys(adapterClass.adsAdapters)) {
            this.getAdapterClasses()[adsAdapter] = new Adapter(
              this._key,
              this._npawVideo,
              this.getPlugin(),
              null,
              this.player,
              new adapterClass.adsAdapters[adsAdapter]()
            );
            lastEvaluatedMethod = 'Ads Adapter -> ' + adsAdapter;
          }
        }
        // Convert additional video adapter contexts to functions
        if (typeof adapterClass.additionalContexts !== 'undefined') {
          for (const context of Object.keys(adapterClass.additionalContexts)) {
            if (!this.getAdapterClasses()[context]) {
              this.getAdapterClasses()[context] = {};
              this.getAdapterClasses()[context].constructor = function () {};
              this.getAdapterClasses()[context].plugin = this.plugin;
              this.getAdapterClasses()[context].getNpawUtils = function () {
                return this.plugin.utils;
              };
            }
            const contextInstance = new adapterClass.additionalContexts[context]();
            const contextPrototype = Object.getPrototypeOf(contextInstance);
            const contextMethods = Object.getOwnPropertyNames(contextPrototype).filter(
              (prop) => typeof contextInstance[prop] === 'function' && prop !== 'constructor'
            );
            for (const method of contextMethods) {
              this.getAdapterClasses()[context][method] = contextInstance[method];
              integratedExternalMethods++;
            }
          }
          lastEvaluatedMethod = 'Additional Contexts';
        }
        this._loadedAdapterImplementation = true;
        // Set the adapter Hash value
        try {
          this.adapterHash = MD5(adapterString.trim());
        } catch (err) {
          this.adapterHash = 'Undefined';
        }
        // Show Adapter loaded
        Log.notice(
          AnalyticsTag,
          '[' +
            this.getAdapterKey() +
            '] Loaded adapter (' +
            this.getVersion() +
            ') (Hash ' +
            this.adapterHash +
            ') ' +
            'implementation from class; From templates: ' +
            loadFromTemplate +
            '; Integrated adapter methods: ' +
            integratedAdapterMethods +
            ', external methods: ' +
            integratedExternalMethods
        );
      } catch (err) {
        Log.error(
          AnalyticsTag,
          "Can't be evaluated adapter class correctly; From templates: " +
            loadFromTemplate +
            ', Last method evaluated: ' +
            lastEvaluatedMethod
        );
      }
    },

    /**
     * Get last used CDN from Core
     */
    getLastUsedCdn: function () {
      return (
        Core.getInstance().getCommonVariable(
          CoreConstants.Products.BALANCER,
          CoreConstants.BalancerVariables.LAST_USED_CDN
        ) || undefined
      );
    },

    /**
     * Override fireStart to add adapter logging for WF version
     * @param {Object} [params] Object of key:value params to add to the request.
     * @param {string} [triggeredEvent]
     */
    fireStart: function (params, triggeredEvent) {
      // Check if player exists to don't fire a start event
      if (this._npawVideo && this._npawVideo.controlPlayerExists() && !this.checkExistsPlayer()) {
        Log.warn(AnalyticsTag, 'Cannot fire start event because player not exists on the document');
        return null;
      }
      if (this.plugin && this.plugin.backgroundDetector && this.plugin.backgroundDetector.canBlockStartCalls()) {
        return null;
      }
      if (!this.flags.isStarted) {
        this.flags.isStarted = true;
        this.chronos.total.start();
        const measureAfterAdBreak = this._npawVideo ? this._npawVideo._joinTimeMeasureAfterAdBreakAvailable() : true;
        if (!this._isAds() && !measureAfterAdBreak) {
          this.chronos.join.start();
        }
        if (triggeredEvent) {
          params = params || {};
          params.triggeredEvents = [triggeredEvent];
        }
        if (this.adapterHash) {
          params = params || {};
          params.pluginHash = this.adapterHash;
        }
        this.startReadyStateMonitor();
        this.emit(AdapterConstants.Event.START, { params: params });
        // If logs is enabled, send adapter method too
        this.plugin._logSetAdapterEvent(this._adapterEvaluatedCode);
      }
    }
  }
);

// Add ads and content adapter mixins
Util.assign(Adapter.prototype, require('./adapter+ads'));
Util.assign(Adapter.prototype, require('./adapter+content'));

module.exports = Adapter;
